import React from 'react';
import { FixedGridItem, ITheme, VisuallyHidden } from '../../../../ui';
import styled from '@emotion/styled';
import { CheckpointQueueDescription } from './CheckpointQueueDescription';
import { CheckpointQueueWaitTimes } from './CheckpointQueueWaitTime';
import { Tile } from './Tile';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { useCheckpointUrl } from './hooks/useCheckpointClickHandler';
import { ICheckpointQueue } from '../../domain/models/ICheckpointQueue';
import Responsive from '../../../../ui/utils/Responsive';

interface ICheckpointQueueProps {
	checkpointQueue: ICheckpointQueue;
}

export const CheckpointQueue = ({ checkpointQueue }: ICheckpointQueueProps) => {
	const { isOpen, name } = checkpointQueue;
	const url = useCheckpointUrl(checkpointQueue);
	const { t } = useTranslation();

	const content = (
		<>
			<CheckpointQueueDescription name={name} isOpen={isOpen} />
			{!isOpen ? (
				<Closed />
			) : (
				<CheckpointQueueWaitTimes
					waitTime={checkpointQueue.formattedWaitTime}
					category={checkpointQueue.waitTimeCategory}
				/>
			)}
		</>
	);

	if (url) {
		return (
			<StyledCheckpointQueueLink
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				role="listitem"
			>
				{content}
				<VisuallyHidden as="span">{t('new_window_aria_label')}</VisuallyHidden>
			</StyledCheckpointQueueLink>
		);
	}

	return (
		<StyledCheckpointQueue role="listitem">{content}</StyledCheckpointQueue>
	);
};

const checkpointQueueStyles = `
	display: flex;
	justify-content: space-between;
	padding: 18px;
	box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.1);

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: 16px;
	}
`;

export const StyledCheckpointQueue = styled(FixedGridItem)<{}, ITheme>`
	${checkpointQueueStyles}
`;

export const StyledCheckpointQueueLink = styled(
	FixedGridItem.withComponent('a'),
)<{}, ITheme>`
	${checkpointQueueStyles}
	text-decoration: none;
	color: inherit;
	box-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.1);
	cursor: pointer;
	transition: box-shadow 0.15s ease-out;

	&:hover {
		box-shadow: 0 20px 30px 0 rgba(0, 0, 0, 0.1);
	}
`;

const Closed = () => {
	const { t } = useTranslation();

	return <Tile variant="closed">{t('security_checkpoint_title_closed')}</Tile>;
};
